/** @NB THIS IS A SHARED MODULE BETWEEN CLIENT & SERVER, DO NOT MAKE DATABASE REQUESTS HERE. */
const SharedSessionConstants = require("./SharedSessionConstants");
const BOOKING_STATUS_CODES = SharedSessionConstants.BOOKING_STATUS_CODES;

const SharedSessionUtilities = {
  isUserBookingDisabled(bookingLimit) {
    return bookingLimit === 0;
  },

  /**
   * Checks whether user is trying to book another session before having completed their first session.
   * @NB Note that this also applies to users who have become premium before having completed their first session.
   */
  isUserBookingWithoutCompletingFirstSession(bookedCount, sessionCount, attemptingToBookCount) {
    if (sessionCount === 0) {
      if (bookedCount === 0 && attemptingToBookCount > 1) {
        return true;
      } else if (bookedCount === 1 && attemptingToBookCount > 0) {
        return true;
      }
    }
    return false;
  },

  hasUserUsedAllFreeSessions(isUserPremium, nrOfUsedFreeSessions) {
    if (isUserPremium) {
      return false; // premium users are not limited by free sessions
    }
    return nrOfUsedFreeSessions >= SharedSessionConstants.MAX_FREE_SESSIONS_WEEK;
  },

  isUserBookingAboveFreeLimit(isUserPremium, nrOfUsedFreeSessions, bookedCount, attemptingToBookCount) {
    if (isUserPremium) {
      return false; // premium users are not limited by free sessions
    }
    return nrOfUsedFreeSessions + bookedCount + attemptingToBookCount > SharedSessionConstants.MAX_FREE_SESSIONS_WEEK;
  },

  /* Either nr of sessions user attempts to book is higher than bookingLimit OR what the user
   * attempts to book is higher in combination on with the already booked than the bookingLimit. */
  isUserBookingAboveLimit(bookedCount, bookingLimit, attemptingToBookCount) {
    const bookingCountMaxedOut = bookedCount >= bookingLimit; // whether user already has maximum number of sessions booked
    const totalBookedAfterMaxedOut = bookedCount + attemptingToBookCount > bookingLimit; // total number of sessions user would have after booking
    return bookingCountMaxedOut || totalBookedAfterMaxedOut;
  },

  isUserAllowedToBook(bookingStatusCode) {
    return bookingStatusCode === BOOKING_STATUS_CODES.BOOKING_ALLOWED;
  },

  /**
   *
   * Selects appropriate booking status code to determine whether user is allowed to book.
   *
   * @param {Object} bookingData
   * @param {Boolean} bookingData.bookingFrozen — Whether user's booking is frozen
   * @param {Boolean} bookingData.firstSessionLimit — Whether user is trying to book another session before having completed their first session
   * @param {Boolean} bookingData.bookingDisabled — Whether user is disabled from booking
   * @param {Boolean} bookingData.hasUsedAllFreeSessions — Whether user has used all free sessions
   * @param {Boolean} bookingData.bookingAboveFreeLimit — Whether user is trying to book above free limit
   *
   * @returns {Number} bookingStatusCode — Code representing whether user is allowed to book
   *
   */
  getBookingStatusCode(bookingData, attemptingToBookCount = 0) {
    const {
      bookingSuspendedByAdmin,
      bookingSuspendedCancellationAbuse,
      bookingFrozen,
      bookedCount,
      sessionCount,
      bookingLimit,
      nrOfUsedFreeSessions,
      isUserPremium,
    } = bookingData;

    /** Initialize booking status codes as being allowed to book */
    let bookingStatusCode = BOOKING_STATUS_CODES.BOOKING_ALLOWED;

    /** Check if an user is limited from booking and set appropriate booking status code.
     * @NB Must be in this order, e.g. if user is suspended, then even if normally
     * they would be allowed to book, then in this case, they should not be able to. */
    if (bookingFrozen) {
      bookingStatusCode = BOOKING_STATUS_CODES.BOOKING_FROZEN;
    } else if (bookingSuspendedByAdmin) {
      bookingStatusCode = BOOKING_STATUS_CODES.BOOKING_SUSPENDED_BY_ADMIN;
    } else if (bookingSuspendedCancellationAbuse) {
      bookingStatusCode = BOOKING_STATUS_CODES.BOOKING_SUSPENDED_CANCELLATION_ABUSE;
    } else if (SharedSessionUtilities.isUserBookingWithoutCompletingFirstSession(bookedCount, sessionCount, attemptingToBookCount)) {
      bookingStatusCode = BOOKING_STATUS_CODES.FIRST_SESSION_LIMIT;
    } else if (SharedSessionUtilities.isUserBookingDisabled(bookingLimit)) {
      bookingStatusCode = BOOKING_STATUS_CODES.BOOKING_DISABLED;
    } else if (SharedSessionUtilities.hasUserUsedAllFreeSessions(isUserPremium, nrOfUsedFreeSessions)) {
      bookingStatusCode = BOOKING_STATUS_CODES.FREE_SESSIONS_USED;
    } else if (SharedSessionUtilities.isUserBookingAboveFreeLimit(isUserPremium, nrOfUsedFreeSessions, bookedCount, attemptingToBookCount)) {
      bookingStatusCode = BOOKING_STATUS_CODES.BOOKING_ABOVE_FREE_LIMIT;
    } else if (SharedSessionUtilities.isUserBookingAboveLimit(bookedCount, bookingLimit, attemptingToBookCount)) {
      bookingStatusCode = BOOKING_STATUS_CODES.BOOKING_ABOVE_LIMIT;
    }

    return bookingStatusCode;
  },
};

module.exports = SharedSessionUtilities;
